import { useCallback, useEffect, useState } from "react";

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * Phase param — deep-links to a specific phase column + job in the DAG.
 * `phase` is the step key (build, security_scan, deploy, verify).
 * `job` is the node id (e.g. "build:api") for the selected job whose logs show.
 */
export interface PipelineNavState {
  run: string | null;
  /** Selected DAG node id (e.g. "build:api"), deep-links to a job's logs. */
  job: string | null;
  /** Phase key for the selected job (build, security_scan, deploy, verify). */
  phase: string | null;
  /** Legacy stage param — kept for backward compatibility with old URLs. */
  stage: string | null;
}

export interface PipelineNavActions {
  setRun: (runId: string | null) => void;
  setJob: (nodeId: string | null) => void;
  setPhase: (phase: string | null) => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const PARAM_RUN = "run";
const PARAM_JOB = "job";
const PARAM_PHASE = "phase";
const PARAM_STAGE = "stage"; // legacy

// ── Helpers ────────────────────────────────────────────────────────────────────

function readParams(): PipelineNavState {
  const sp = new URLSearchParams(window.location.search);
  return {
    run: sp.get(PARAM_RUN) ?? null,
    job: sp.get(PARAM_JOB) ?? null,
    phase: sp.get(PARAM_PHASE) ?? null,
    stage: sp.get(PARAM_STAGE) ?? null,
  };
}

function writeParams(updates: Record<string, string | null>, push = false) {
  const sp = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null) sp.delete(key);
    else sp.set(key, value);
  }
  const url =
    window.location.pathname + (sp.toString() ? "?" + sp.toString() : "");
  if (push) {
    window.history.pushState(null, "", url);
  } else {
    window.history.replaceState(null, "", url);
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * URL-driven pipeline navigation state.
 *
 * Reads `run`, `job`, `phase`, `env` from URL query params and writes
 * them back via `history.replaceState` on user interaction. Navigation
 * survives page reload and browser back/forward.
 *
 * `job` is the DAG node id (e.g. "build:api") — deep-links to a specific
 * job's logs. `phase` is the step key (build, deploy, etc.) for the column.
 *
 * Bypasses react-router's `useSearchParams` to avoid stale-URL bugs when
 * multiple params change in the same tick. Uses `popstate` listener to
 * detect browser back/forward.
 */
export function usePipelineSearchParams(): PipelineNavState &
  PipelineNavActions {
  const [localState, setLocalState] = useState<PipelineNavState>(readParams);

  // Listen for browser back/forward (popstate)
  useEffect(() => {
    const handler = () => setLocalState(readParams());
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // Helper: update both local state and URL atomically
  const updateParams = useCallback(
    (updates: Record<string, string | null>, push = false) => {
      setLocalState((prev) => {
        const next = { ...prev };
        if (PARAM_RUN in updates) next.run = updates[PARAM_RUN] ?? null;
        if (PARAM_JOB in updates) next.job = updates[PARAM_JOB] ?? null;
        if (PARAM_PHASE in updates) next.phase = updates[PARAM_PHASE] ?? null;
        if (PARAM_STAGE in updates) next.stage = updates[PARAM_STAGE] ?? null;
        return next;
      });
      writeParams(updates, push);
    },
    [],
  );

  // ── Setters ────────────────────────────────────────────────────────────────

  // Run selection creates a new history entry (major navigation).
  // Clears job + phase so the new run auto-selects its own default job.
  const setRun = useCallback(
    (runId: string | null) => {
      const updates: Record<string, string | null> = {};
      if (runId) {
        updates[PARAM_RUN] = runId;
        const currentRun = new URLSearchParams(window.location.search).get(
          PARAM_RUN,
        );
        if (runId !== currentRun) {
          updates[PARAM_JOB] = null;
          updates[PARAM_PHASE] = null;
        }
      } else {
        updates[PARAM_RUN] = null;
        updates[PARAM_JOB] = null;
        updates[PARAM_PHASE] = null;
      }
      updateParams(updates, true); // push = new history entry
    },
    [updateParams],
  );

  // Job selection (node id) replaces the current entry (minor navigation).
  const setJob = useCallback(
    (nodeId: string | null) => {
      updateParams({ [PARAM_JOB]: nodeId }); // replace = no new history entry
    },
    [updateParams],
  );

  // Phase selection replaces the current entry (minor navigation).
  const setPhase = useCallback(
    (phase: string | null) => {
      updateParams({ [PARAM_PHASE]: phase }); // replace = no new history entry
    },
    [updateParams],
  );

  return { ...localState, setRun, setJob, setPhase };
}
